import { AVATAR_EMOJI, PROTOCOL } from '@uno/shared';
import { useState } from 'react';
import { useT } from '../hooks';

interface Props {
  initial: string;
  initialEmoji: string;
  /** True before the visitor has ever chosen a name. */
  firstRun?: boolean;
  onSubmit: (nickname: string, emoji: string) => void;
  /** Absent on first run — a nickname is required to continue. */
  onCancel?: () => void;
}

export function NicknameGate({ initial, initialEmoji, firstRun = false, onSubmit, onCancel }: Props) {
  const t = useT();
  const [value, setValue] = useState(initial);
  const [emoji, setEmoji] = useState(initialEmoji);
  const trimmed = value.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= PROTOCOL.NICKNAME_MAX;

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form
        className="modal modal--sm"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onSubmit(trimmed, emoji);
        }}
      >
        <h2 className="modal__title">{firstRun ? t.nickname.pickTitle : t.nickname.changeTitle}</h2>
        <p className="muted">{t.nickname.subtitle}</p>

        <div className="field-row" style={{ alignItems: 'center', gap: 10 }}>
          <span className={`avatar ${emoji ? 'avatar--emoji' : ''}`} aria-hidden="true">
            {emoji || (trimmed.slice(0, 1).toUpperCase() || '?')}
          </span>
          <input
            className="input input--lg"
            name="nickname"
            autoFocus
            value={value}
            maxLength={PROTOCOL.NICKNAME_MAX}
            placeholder={t.nickname.placeholder}
            onChange={(e) => setValue(e.target.value)}
            onFocus={(e) => firstRun && e.target.select()}
            aria-label={t.nickname.label}
          />
        </div>

        <label className="field">
          <span className="field__label">{t.profile.avatar}</span>
          <span className="field__hint">{t.profile.avatarHint}</span>
        </label>

        {/* The bot's robot face is deliberately absent — see AVATAR_EMOJI. */}
        <div className="emojigrid" role="group" aria-label={t.profile.avatar}>
          <button
            type="button"
            aria-pressed={emoji === ''}
            onClick={() => setEmoji('')}
            title={t.profile.avatar}
          >
            {trimmed.slice(0, 1).toUpperCase() || '?'}
          </button>
          {AVATAR_EMOJI.map((e) => (
            <button key={e} type="button" aria-pressed={emoji === e} onClick={() => setEmoji(e)}>
              {e}
            </button>
          ))}
        </div>

        <div className="modal__actions">
          {onCancel && (
            <button type="button" className="btn btn--ghost" onClick={onCancel}>
              {t.common.cancel}
            </button>
          )}
          <button type="submit" className="btn btn--primary" disabled={!valid}>
            {firstRun ? t.nickname.letsPlay : t.common.save}
          </button>
        </div>
      </form>
    </div>
  );
}
